/**
 * Dynamic Skeleton Matching System
 * Automatically generates loading skeletons that mimic UI component layouts
 * 
 * Usage:
 * <DynamicSkeleton component="card" variant="recipe" />
 * <DynamicSkeleton component="list" variant="mealplan" count={3} />
 * <SkeletonContainer loading={isLoading}><ActualComponent /></SkeletonContainer>
 */

import React from 'react';
import { cn } from './utils';
import { Skeleton } from './skeleton';

// =========================
// Core Types and Interfaces
// =========================

export type SkeletonVariant = 
  | 'default'
  | 'card'
  | 'list'
  | 'table'
  | 'form'
  | 'navigation'
  | 'media'
  | 'data'
  | 'button'
  | 'text';

export type ComponentVariant =
  | 'default'
  | 'recipe'
  | 'mealplan'
  | 'admin'
  | 'dashboard'
  | 'profile'
  | 'billing'
  | 'metric'
  | 'search'
  | 'compact'
  | 'detailed'
  | 'sidebar'
  | 'breadcrumb'
  | 'tabs';

type SkeletonTemplate = () => JSX.Element;

type TemplateMap = Partial<Record<ComponentVariant, SkeletonTemplate>> & {
  default?: SkeletonTemplate;
};

type SkeletonTemplateGroup = SkeletonTemplate | TemplateMap;

export interface SkeletonProps {
  /** Primary skeleton type */
  component?: SkeletonVariant;
  /** Component-specific variant */
  variant?: ComponentVariant;
  /** Number of skeleton items to render */
  count?: number;
  /** Responsive behavior */
  responsive?: boolean;
  /** Custom className */
  className?: string;
  /** Animation type */
  animation?: 'pulse' | 'shimmer' | 'wave' | 'none';
  /** Custom dimensions */
  dimensions?: {
    width?: string;
    height?: string;
  };
}

// =========================
// Skeleton Templates
// =========================

/**
 * Card skeleton templates
 */
export const CardSkeletons = {
  recipe: () => (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <Skeleton className="h-48 w-full" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <div className="flex justify-between items-center pt-2">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-14" />
          </div>
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-18 rounded-full" />
        </div>
      </div>
    </div>
  ),

  mealplan: () => (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-16 w-full rounded" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="flex justify-between items-center">
        <Skeleton className="h-4 w-20" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </div>
  ),

  admin: () => (
    <div className="bg-white rounded-lg border shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  ),

  dashboard: () => (
    <div className="bg-white rounded-lg border shadow-sm p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  ),

  metric: () => (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-4 rounded-full" />
      </div>
      <Skeleton className="h-8 w-24 mb-2" />
      <Skeleton className="h-3 w-16" />
    </div>
  )
} satisfies TemplateMap;

/**
 * List skeleton templates
 */
export const ListSkeletons = {
  mealplan: () => (
    <div className="space-y-4">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-lg border">
          <Skeleton className="h-16 w-16 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </div>
          <div className="text-right space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
      ))}
    </div>
  ),

  admin: () => (
    <div className="space-y-2">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex items-center gap-4 p-3 bg-white rounded border">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-12" />
        </div>
      ))}
    </div>
  ),

  compact: () => (
    <div className="space-y-2">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
} satisfies TemplateMap;

/**
 * Table skeleton templates
 */
export const TableSkeletons = {
  admin: () => (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <div className="p-4 border-b">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {Array.from({ length: 5 }, (_, i) => (
                <th key={i} className="px-4 py-3">
                  <Skeleton className="h-4 w-20" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }, (_, i) => (
              <tr key={i} className="border-t">
                {Array.from({ length: 5 }, (_, j) => (
                  <td key={j} className="px-4 py-3">
                    <Skeleton className="h-4 w-24" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ),

  default: () => (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-4 p-2 bg-gray-50 rounded">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-4 w-20" />
        ))}
      </div>
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="grid grid-cols-4 gap-4 p-2">
          {Array.from({ length: 4 }, (_, j) => (
            <Skeleton key={j} className="h-4 w-24" />
          ))}
        </div>
      ))}
    </div>
  )
} satisfies TemplateMap;

/**
 * Form skeleton templates
 */
export const FormSkeletons = {
  profile: () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-full" />
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-20" />
      </div>
    </div>
  ),

  default: () => (
    <div className="space-y-4">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      <div className="flex gap-3">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-20" />
      </div>
    </div>
  )
} satisfies TemplateMap;

/**
 * Navigation skeleton templates
 */
export const NavigationSkeletons = {
  sidebar: () => (
    <div className="space-y-4 p-4">
      <div className="space-y-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  ),

  breadcrumb: () => (
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-16" />
      <span className="text-gray-400">/</span>
      <Skeleton className="h-4 w-20" />
      <span className="text-gray-400">/</span>
      <Skeleton className="h-4 w-24" />
    </div>
  ),

  tabs: () => (
    <div className="flex gap-4 border-b">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-8 w-20" />
      ))}
    </div>
  )
} satisfies TemplateMap;

// =========================
// Template Mapping System
// =========================

const SKELETON_TEMPLATES = {
  default: {
    default: () => (
      <div className="h-6 w-24 animate-pulse rounded bg-muted" />
    )
  },
  card: CardSkeletons,
  list: ListSkeletons,
  table: TableSkeletons,
  form: FormSkeletons,
  navigation: NavigationSkeletons,

  // Simple patterns
  button: () => <Skeleton className="h-10 w-24 rounded" />,
  text: () => (
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
    </div>
  ),
  media: () => <Skeleton className="h-48 w-full rounded" />,
  data: () => (
    <div className="flex items-center justify-between">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-8 w-16" />
    </div>
  )
} satisfies Record<SkeletonVariant, SkeletonTemplateGroup>;

const resolveTemplate = (
  group: SkeletonTemplateGroup,
  variant: ComponentVariant
): SkeletonTemplate | undefined => {
  if (typeof group === 'function') {
    return group;
  }

  const variantTemplate = group[variant];
  if (typeof variantTemplate === 'function') {
    return variantTemplate;
  }

  if (typeof group.default === 'function') {
    return group.default;
  }

  for (const template of Object.values(group) as Array<SkeletonTemplate | undefined>) {
    if (typeof template === 'function') {
      return template;
    }
  }

  return undefined;
};

// =========================
// Dynamic Skeleton Component
// =========================

export function DynamicSkeleton({
  component = 'default',
  variant = 'default',
  count = 1,
  responsive = true,
  className,
  animation = 'pulse',
  dimensions
}: SkeletonProps) {
  const TemplateGroup = SKELETON_TEMPLATES[component];

  if (!TemplateGroup) {
    // Fallback to basic skeleton
    return (
      <Skeleton
        className={cn(
          'h-4 w-full',
          className
        )}
        style={dimensions}
      />
    );
  }

  // Get the specific template
  const Template = resolveTemplate(TemplateGroup, variant);

  if (!Template) {
    return (
      <Skeleton
        className={cn(
          'h-4 w-full',
          className
        )}
        style={dimensions}
      />
    );
  }

  // Render single or multiple instances
  if (count === 1) {
    return (
      <div className={cn(responsive && "w-full", className)}>
        <Template />
      </div>
    );
  }

  return (
    <div className={cn(
      'space-y-4',
      responsive && "w-full",
      className
    )}>
      {Array.from({ length: count }, (_, i) => (
        <Template key={i} />
      ))}
    </div>
  );
}

// =========================
// Skeleton Container HOC
// =========================

interface SkeletonContainerProps {
  loading: boolean;
  skeleton?: React.ReactNode;
  skeletonProps?: SkeletonProps;
  children: React.ReactNode;
  className?: string;
}

export function SkeletonContainer({
  loading,
  skeleton,
  skeletonProps,
  children,
  className
}: SkeletonContainerProps) {
  if (loading) {
    return (
      <div className={className}>
        {skeleton || <DynamicSkeleton {...skeletonProps} />}
      </div>
    );
  }

  return <>{children}</>;
}

// =========================
// Utility Hooks
// =========================

/**
 * Hook to automatically determine skeleton props based on component props
 */
export function useSkeletonProps(componentType: string, props: any): SkeletonProps {
  // Intelligent mapping based on component types and props
  if (componentType.includes('Recipe')) {
    return { component: 'card', variant: 'recipe' };
  }
  
  if (componentType.includes('MealPlan')) {
    return { component: 'card', variant: 'mealplan' };
  }
  
  if (componentType.includes('Admin')) {
    return { component: 'card', variant: 'admin' };
  }
  
  if (componentType.includes('List')) {
    return { 
      component: 'list', 
      variant: props.variant || 'default',
      count: props.items?.length || 3
    };
  }
  
  if (componentType.includes('Table')) {
    return { component: 'table', variant: 'admin' };
  }
  
  if (componentType.includes('Form')) {
    return { component: 'form', variant: 'default' };
  }
  
  // Default fallback
  return { component: 'default' };
}

// =========================
// Export all components
// =========================

export default DynamicSkeleton;